import React from 'react';
import { Badge, Button } from '../ui';
import { Save, Plus, AlertCircle, Info, Trash2, TrendingUp } from 'lucide-react';
export function FeeManagementView() {
  return <div className="space-y-8" data-id="element-247">
      {/* Header Section */}
      <div className="flex justify-between items-start" data-id="element-248">
        <div data-id="element-249">
          <h2 className="text-3xl font-serif font-bold text-bank-navy" data-id="element-250">
            Fee Management
          </h2>
          <p className="text-lg text-slate-500 mt-2" data-id="element-251">
            Configure processing fees, interchange rates, and fee tier
            structures by currency.
          </p>
        </div>
        <Button variant="primary" className="bg-bank-navy hover:bg-bank-navy-light" leftIcon={<Save className="h-4 w-4" data-id="element-253" />} data-id="element-252">
          Save Changes
        </Button>
      </div>

      {/* Maker-Checker Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex justify-between items-center" data-id="element-254">
        <div className="flex items-start space-x-3" data-id="element-255">
          <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" data-id="element-256" />
          <div data-id="element-257">
            <p className="text-sm text-amber-900" data-id="element-258">
              Workflow Status: <span className="font-bold" data-id="element-259">PENDING</span>
            </p>
            <p className="text-xs text-amber-600 mt-0.5" data-id="element-260">
              Last updated: Today, 11:15 AM
            </p>
          </div>
        </div>
        <div className="text-right text-sm text-amber-900" data-id="element-261">
          <p data-id="element-262">
            <span className="font-bold" data-id="element-263">Maker:</span> Operator_C
          </p>
          <p data-id="element-264">
            <span className="font-bold" data-id="element-265">Checker:</span> Supervisor_A
          </p>
        </div>
      </div>

      {/* Main Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-266">
        {/* Left Column - Configuration */}
        <div className="lg:col-span-2 space-y-8 bg-white p-6 rounded-xl border border-slate-200 shadow-sm" data-id="element-267">
          {/* Fee Tier Config Header */}
          <div className="flex justify-between items-start" data-id="element-268">
            <div data-id="element-269">
              <h3 className="text-xl font-serif font-bold text-bank-navy" data-id="element-270">
                Fee Tier Configuration
              </h3>
              <p className="text-slate-500 mt-1" data-id="element-271">
                Set processing and interchange fees by currency with advanced
                tiering logic.
              </p>
            </div>
            <Button variant="secondary" className="bg-bank-gold text-bank-navy hover:bg-bank-gold-light" leftIcon={<Plus className="h-4 w-4" data-id="element-273" />} data-id="element-272">
              Add Tier
            </Button>
          </div>

          {/* Form Fields */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-274">
            <div data-id="element-275">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-276">
                Currency Code <span className="text-red-500" data-id="element-277">*</span>
              </label>
              <input type="text" placeholder="e.g., 044, 230, 840" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bank-navy/20 focus:border-bank-navy" data-id="element-278" />
              <p className="text-xs text-slate-400 mt-1" data-id="element-279">
                3-digit ISO code (e.g., 044 for BSD, 230 for ETB)
              </p>
            </div>
            <div data-id="element-280">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-281">
                Calculation Method
              </label>
              <select className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bank-navy/20 focus:border-bank-navy bg-white" data-id="element-282">
                <option data-id="element-283">Tiered</option>
                <option data-id="element-284">Flat Rate</option>
                <option data-id="element-285">Percentage</option>
              </select>
            </div>
            <div data-id="element-286">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-287">
                Calculation Base
              </label>
              <select className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bank-navy/20 focus:border-bank-navy bg-white" data-id="element-288">
                <option data-id="element-289">Always reflect Incoming Amount</option>
                <option data-id="element-290">Reflect Outgoing Amount</option>
              </select>
            </div>
          </div>

          {/* Tiered Logic Thresholds */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-5" data-id="element-291">
            <div className="flex items-center space-x-2 mb-4" data-id="element-292">
              <Info className="h-4 w-4 text-bank-navy" data-id="element-293" />
              <h4 className="font-bold text-bank-navy text-sm" data-id="element-294">
                Tiered Logic Thresholds
              </h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-295">
              <div data-id="element-296">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-297">
                  Count Threshold
                </label>
                <input type="text" defaultValue="0" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bank-navy/20 focus:border-bank-navy" data-id="element-298" />
                <p className="text-xs text-slate-400 mt-1" data-id="element-299">
                  Minimum transaction count to apply tier
                </p>
              </div>
              <div data-id="element-300">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-301">
                  Sum Threshold
                </label>
                <input type="text" defaultValue="0" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bank-navy/20 focus:border-bank-navy" data-id="element-302" />
                <p className="text-xs text-slate-400 mt-1" data-id="element-303">
                  Minimum cumulative amount to apply tier
                </p>
              </div>
            </div>
          </div>

          {/* Current Tiers */}
          <div className="space-y-4" data-id="element-304">
            <div className="flex justify-between items-center border-b border-slate-100 pb-2" data-id="element-305">
              <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wider" data-id="element-306">
                Current Tiers (BSD 044)
              </h4>
              <Badge variant="info" data-id="element-307">3 Active Tiers</Badge>
            </div>

            {/* Tier Card */}
            <div className="border border-slate-200 rounded-lg p-5 space-y-4" data-id="element-308">
              <div className="grid grid-cols-1 md:grid-cols-7 gap-4 items-end" data-id="element-309">
                <div className="md:col-span-2" data-id="element-310">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-311">
                    Min Amount
                  </label>
                  <input type="text" defaultValue="0.00" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bank-navy/20 focus:border-bank-navy" data-id="element-312" />
                </div>
                <div className="md:col-span-2" data-id="element-313">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-314">
                    Max Amount
                  </label>
                  <input type="text" defaultValue="500.00" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bank-navy/20 focus:border-bank-navy" data-id="element-315" />
                </div>
                <div className="md:col-span-2" data-id="element-316">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-317">
                    Fee
                  </label>
                  <input type="text" defaultValue="$0.50" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bank-navy/20 focus:border-bank-navy" data-id="element-318" />
                </div>
                <div className="flex justify-end pb-2" data-id="element-319">
                  <button className="text-red-400 hover:text-red-600 transition-colors" data-id="element-320">
                    <Trash2 className="h-5 w-5" data-id="element-321" />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-slate-50" data-id="element-322">
                <div data-id="element-323">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-324">
                    Effective From
                  </label>
                  <input type="date" defaultValue="2026-01-01" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bank-navy/20 focus:border-bank-navy text-slate-600" data-id="element-325" />
                </div>
                <div data-id="element-326">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-327">
                    Effective To
                  </label>
                  <input type="date" defaultValue="2026-12-31" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bank-navy/20 focus:border-bank-navy text-slate-600" data-id="element-328" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Summary */}
        <div className="space-y-6" data-id="element-329">
          {/* Fee Summary Card */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-id="element-330">
            <h3 className="text-xl font-serif font-bold text-bank-navy" data-id="element-331">
              Fee Summary
            </h3>
            <p className="text-slate-500 mt-1 mb-6" data-id="element-332">
              Current fee structure overview.
            </p>

            <div className="space-y-6" data-id="element-333">
              <div className="pb-6 border-b border-slate-100" data-id="element-334">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-335">
                  Total Fees Collected (Today)
                </p>
                <p className="text-3xl font-bold text-bank-navy mb-1" data-id="element-336">
                  $12,847.50
                </p>
                <div className="flex items-center text-sm font-medium text-green-600" data-id="element-337">
                  <TrendingUp className="h-4 w-4 mr-1" data-id="element-338" />
                  <span data-id="element-339">+8.2% vs yesterday</span>
                </div>
              </div>
              <div className="pb-6 border-b border-slate-100" data-id="element-340">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-341">
                  Average Fee per Transaction
                </p>
                <p className="text-3xl font-bold text-bank-navy" data-id="element-342">$0.89</p>
              </div>
              <div data-id="element-343">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-344">
                  Transactions Processed
                </p>
                <p className="text-3xl font-bold text-bank-navy" data-id="element-345">14,438</p>
              </div>
            </div>
          </div>

          {/* Interchange Rates Card */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-id="element-346">
            <h3 className="text-xl font-serif font-bold text-bank-navy mb-4" data-id="element-347">
              Interchange Rates
            </h3>
            <div className="space-y-1" data-id="element-348">
              <div className="flex justify-between items-center py-3 border-b border-slate-100" data-id="element-349">
                <span className="text-slate-700 font-medium" data-id="element-350">P2P Transfer</span>
                <span className="font-mono font-bold text-bank-navy" data-id="element-351">
                  0.15%
                </span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-slate-100" data-id="element-352">
                <span className="text-slate-700 font-medium" data-id="element-353">Bill Payment</span>
                <span className="font-mono font-bold text-bank-navy" data-id="element-354">
                  0.25%
                </span>
              </div>
              <div className="flex justify-between items-center py-3" data-id="element-355">
                <span className="text-slate-700 font-medium" data-id="element-356">
                  Merchant Payment
                </span>
                <span className="font-mono font-bold text-bank-navy" data-id="element-357">
                  0.35%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}